import { config } from '../../utils/config';
import { identityAuth } from '../../auth/IdentityAuth';

const SEND_SET_PASSWORD_PATH = '/api/v1/auth/send-set-password-link';

/** RFC-style pragmatic check (local@domain.tld) */
export function isValidEmail(value) {
  const s = value?.trim();
  if (!s) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i.test(s);
}

/** Standard 8-4-4-4-12 UUID (any version) */
export function isValidUuid(value) {
  const s = value?.trim();
  if (!s) return false;
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s);
}

/**
 * Only includes properties that pass validation (never sends invalid email or malformed GUID).
 * @returns {{ email?: string; userId?: string }}
 */
export function buildSendSetPasswordLinkBody(payload) {
  const email = payload.email?.trim() ?? '';
  const userId = payload.userId?.trim() ?? '';
  const body = {};
  if (isValidEmail(email)) body.email = email;
  if (isValidUuid(userId)) body.userId = userId;
  return body;
}

function emptyBodyErrorMessage(emailRaw, userIdRaw) {
  const email = emailRaw?.trim() ?? '';
  const userId = userIdRaw?.trim() ?? '';
  if (email && !isValidEmail(email)) return 'Enter a valid email address.';
  if (userId && !isValidUuid(userId)) return 'User ID is not a valid GUID.';
  if (!email && !userId) return 'Enter a valid email address.';
  return 'Enter a valid email address or user ID.';
}

/**
 * Admin: send invite (no password) or reset (has password) email via Identity API.
 * @param {{ email?: string; userId?: string }} payload — only valid email and/or valid UUID are sent
 * @returns {Promise<object>}
 */
export async function sendSetPasswordLink(payload) {
  const baseUrl = config.IDENTITY_API_URL;
  if (!baseUrl) {
    throw new Error(
      'Identity API URL is not configured. Set REACT_APP_IDENTITY_API_URL (or REACT_APP_API_URL) in your environment.'
    );
  }

  const token = await identityAuth.getValidToken();
  if (!token) {
    throw new Error('You must be signed in to use this tool.');
  }

  const body = buildSendSetPasswordLinkBody(payload);
  if (Object.keys(body).length === 0) {
    throw new Error(emptyBodyErrorMessage(payload.email, payload.userId));
  }

  const url = `${baseUrl.replace(/\/$/, '')}${SEND_SET_PASSWORD_PATH}`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });

  const text = await res.text();
  let data = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = { message: text };
    }
  }

  if (!res.ok) {
    const err = new Error(
      data?.detail || data?.title || data?.message || `Request failed (${res.status})`
    );
    err.status = res.status;
    err.data = data;
    throw err;
  }

  return data || {};
}

const identityAdminService = {
  sendSetPasswordLink,
};

export default identityAdminService;
